import { type ApolloError } from '@apollo/client'

/**
 * The keyed guards reject with two user-facing messages: 'Nøkkel ikke gyldig'
 * (unknown key) and 'Polf Event er avsluttet…' (event closed, or keyed access
 * past the grace period — `isEventAccessExpired` in the api). The second means
 * the link itself was valid, so shells must show "tournament over" rather than
 * invalid-link advice. The prefix match is coupled to the guard messages in
 * `apps/api/src/auth/guards/{player-key,beatthepro-key}.guard.ts`.
 */
export const isEventOverError = (error: ApolloError | undefined): boolean =>
  !!error?.graphQLErrors.some((e) =>
    e.message.startsWith('Polf Event er avsluttet')
  )

const isValidationCode = (code: unknown): boolean =>
  code === 'GRAPHQL_VALIDATION_FAILED'

/**
 * Stale-bundle class: the server rejected one of our own generated queries
 * (a field this bundle selects no longer exists in the deployed schema). The
 * queries carry no user input, so a validation failure can only mean the
 * bundle predates the schema — the cure is an app update, not a new link.
 * Apollo surfaces the errors either as `graphQLErrors` (200-with-errors) or
 * as a ServerError `networkError` (HTTP 400) whose `result` carries them.
 */
export const isSchemaMismatchError = (
  error: ApolloError | undefined
): boolean => {
  if (!error) return false
  if (error.graphQLErrors.some((e) => isValidationCode(e.extensions?.code))) {
    return true
  }
  const net = error.networkError as {
    statusCode?: number
    result?: unknown
  } | null
  if (net?.statusCode !== 400) return false
  const errors =
    net.result && typeof net.result === 'object'
      ? (net.result as { errors?: unknown }).errors
      : undefined
  return (
    Array.isArray(errors) &&
    errors.some((e) =>
      isValidationCode(
        (e as { extensions?: { code?: unknown } } | null)?.extensions?.code
      )
    )
  )
}
